import { type ReactNode, type Ref } from 'react';
import { FeedCardMedia } from './feed-card-media';
import { FeedCardEmbeds } from './feed-card-embeds';
import { sanitizeBodyHtml } from './sanitize';
import type { FeedItem } from './types';

type Props = {
  item: FeedItem;
  collectionId?: string;
  showUnreadDot?: boolean;
  authorLabel?: string;
  timestamp?: string;
  actions?: ReactNode;
  headerRef?: Ref<HTMLElement>;
};

const TITLE = 'text-[18px] font-semibold leading-snug text-text-primary';
const META = 'flex items-center gap-xs text-[12px] text-text-tertiary';
const PROSE =
  'mt-xs text-[15px] leading-relaxed text-text-secondary [&_a]:text-info [&_a:hover]:underline [&_h2]:mt-md [&_h2]:text-[16px] [&_h2]:font-semibold [&_h2]:text-text-primary [&_h3]:mt-md [&_h3]:text-[14px] [&_h3]:font-semibold [&_h3]:text-text-primary [&_ul]:list-disc [&_ul]:pl-md [&_ol]:list-decimal [&_ol]:pl-md [&_blockquote]:border-l-2 [&_blockquote]:border-white/[0.1] [&_blockquote]:pl-sm [&_blockquote]:text-text-tertiary';

type HeaderProps = {
  authorLabel?: string | undefined;
  timestamp?: string | undefined;
  showUnreadDot?: boolean | undefined;
  actions?: ReactNode;
  headerRef?: Ref<HTMLElement> | undefined;
};

function HeaderRight(
  p: Pick<HeaderProps, 'showUnreadDot' | 'actions'>
): ReactNode {
  return (
    <div className="ml-auto flex items-center gap-xs">
      {p.showUnreadDot && (
        <span className="inline-block h-2 w-2 rounded-full bg-brand-red" />
      )}
      {p.actions}
    </div>
  );
}

function CardHeader(p: HeaderProps): ReactNode {
  return (
    <header ref={p.headerRef} className={META}>
      {p.authorLabel && (
        <span className="text-text-secondary">{p.authorLabel}</span>
      )}
      {p.authorLabel && p.timestamp && <span aria-hidden>·</span>}
      {p.timestamp && <span>{p.timestamp}</span>}
      <HeaderRight showUnreadDot={p.showUnreadDot} actions={p.actions} />
    </header>
  );
}

function CardBody({ html }: { html: string }): ReactNode {
  if (!html) return null;
  return (
    <div
      className={PROSE}
      dangerouslySetInnerHTML={{ __html: sanitizeBodyHtml(html) }}
    />
  );
}

export function FeedCard(p: Props): ReactNode {
  const collectionId = p.collectionId ?? 'comunicados';
  return (
    <article className="py-xl">
      <CardHeader
        authorLabel={p.authorLabel}
        timestamp={p.timestamp}
        showUnreadDot={p.showUnreadDot}
        actions={p.actions}
        headerRef={p.headerRef}
      />
      {p.item.title && <h2 className={`${TITLE} mt-xs`}>{p.item.title}</h2>}
      <CardBody html={p.item.body_html} />
      <FeedCardMedia
        collectionId={collectionId}
        recordId={p.item.id}
        files={p.item.media}
      />
      <FeedCardEmbeds embeds={p.item.embeds} />
    </article>
  );
}
